import React, { useState, useEffect } from 'react';
import { BackupService, ImportResult } from '../services/backupService';
import { isMobile, getExportButtonText, getImportButtonText, getFileOperationTips } from '../utils/mobileDetection';
import { useStartOverModal } from '../context/StartOverModalContext';
import { useStepWorkspace } from '../context/StepWorkspaceContext';
import { useLocale } from '../context/LocaleContext';
import { getActiveLocale } from '../i18n/translate';
import styles from '../styles/backupRestoreModal.module.css';

interface BackupRestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProgramTrack {
  id: string;
  label: string;
  program: string;
  track: string;
}

// Backup Modal Component
const BackupModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  programTrack: ProgramTrack;
}> = ({ isOpen, onClose, programTrack }) => {
  const { t } = useLocale();
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [filename, setFilename] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clearAfterBackup, setClearAfterBackup] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [dataWasCleared, setDataWasCleared] = useState(false);
  const [showNoDataModal, setShowNoDataModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const defaultFilename = BackupService.generateDefaultFilename(
        programTrack.program, 
        programTrack.track
      );
      setFilename(defaultFilename);
      setError(null);
      setClearAfterBackup(false);
    }
  }, [isOpen, programTrack]);

  const handleBackup = async () => {
    if (!filename.trim()) {
      setError(t('backup.backup.error.filename_required'));
      return;
    }

    // Check if there's any data to backup
    try {
      const hasData = await BackupService.hasCurrentData(programTrack.program, programTrack.track);
      if (!hasData) {
        setShowNoDataModal(true);
        return;
      }
    } catch (error) {
      setError(t('backup.backup.error.check_failed'));
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const result = await BackupService.exportProgress(
        programTrack.program, 
        programTrack.track, 
        filename
      );
      
      if (result.success) {
        let message = t('backup.backup.completed');
        
        // Clear data after backup if checkbox is checked
        if (clearAfterBackup) {
          try {
            const clearResult = await BackupService.clearProgress(
              programTrack.program, 
              programTrack.track
            );
            
            if (clearResult.success) {
              message = t('backup.backup.completed_and_cleared');
              setDataWasCleared(true);
            } else {
              message = t('backup.backup.completed_clear_failed', { error: String(clearResult.error) });
              setDataWasCleared(false);
            }
          } catch (clearError) {
            message = t('backup.backup.completed_clear_failed', { error: String(clearError) });
            setDataWasCleared(false);
          }
        } else {
          setDataWasCleared(false);
        }
        
        setSuccessMessage(message);
        setShowSuccess(true);
      } else {
        setError(result.error || t('backup.backup.failed'));
      }
    } catch (error) {
      setError(t('backup.backup.failed_with_reason', { error: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessOk = () => {
    if (dataWasCleared) {
      notifyStepWorkspaceChanged();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>📤 {t('backup.backup.title')}</h3>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>
          
          {showSuccess ? (
            <div className={styles.section}>
              <div className={styles.successMessage}>
                ✅ {successMessage}
              </div>
              <div className={styles.actionControls}>
                <button
                  onClick={handleSuccessOk}
                  className={styles.primaryButton}
                >
                  {t('dialog.ok')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.section}>
                <p>{t('backup.backup.intro', { label: programTrack.label })}</p>
                
                <div className={styles.inputGroup}>
                  <label htmlFor="filename">{t('backup.backup.filename_label')}</label>
                  <input
                    id="filename"
                    type="text"
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    placeholder={t('backup.backup.filename_placeholder')}
                    className={styles.filenameInput}
                  />
                </div>
                
                {/* Laptop backup experience */}
                {!isMobile() && (
                  <div className={styles.laptopInfo}>
                    <p><strong>📁 {t('backup.backup.location_label')}</strong> {t('backup.backup.location_text')}</p>
                    <p><strong>🔐 {t('backup.backup.encryption_label')}</strong> {t('backup.backup.encryption_text')}</p>
                  </div>
                )}
                
                {/* Clear after backup checkbox */}
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={clearAfterBackup}
                      onChange={(e) => setClearAfterBackup(e.target.checked)}
                      className={styles.checkbox}
                    />
                    {t('backup.backup.clear_after')}
                  </label>
                </div>
                
                <div className={styles.actionControls}>
                  <button
                    onClick={handleBackup}
                    disabled={isLoading || !filename.trim()}
                    className={styles.primaryButton}
                  >
                    {isLoading ? t('backup.processing') : getExportButtonText()}
                  </button>
                  <button
                    onClick={onClose}
                    className={styles.secondaryButton}
                  >
                    {t('dialog.cancel')}
                  </button>
                </div>
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  ❌ {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* No Data Modal */}
      {showNoDataModal && (
        <div className={styles.modalOverlay} onClick={() => setShowNoDataModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>ℹ️ {t('backup.no_data.title')}</h3>
              <button className={styles.closeButton} onClick={() => setShowNoDataModal(false)}>×</button>
            </div>
            <div className={styles.section}>
              <p>{t('backup.no_data.message')}</p>
              <div className={styles.actionControls}>
                <button
                  onClick={() => setShowNoDataModal(false)}
                  className={styles.primaryButton}
                >
                  {t('dialog.ok')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Restore Modal Component
const RestoreModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  programTrack: ProgramTrack;
  hasCurrentData: boolean;
}> = ({ isOpen, onClose, programTrack, hasCurrentData }) => {
  const { t } = useLocale();
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showRestorePreview, setShowRestorePreview] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
      setOverwriteExisting(false);
      setShowRestorePreview(false);
      setImportResult(null);
    }
  }, [isOpen]);

  const handleRestore = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await BackupService.importProgress();
      
      if (result.success) {
        setImportResult(result);
        setShowRestorePreview(true);
      } else {
        setError(result.error || t('backup.restore.failed'));
      }
    } catch (error) {
      setError(t('backup.restore.failed_with_reason', { error: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const buildRestoreResultMessage = (
    heading: string,
    results: { successful: number; warnings: number; skipped: number }
  ) => {
    const { successful, warnings, skipped } = results;
    let message = `${heading}\n\n`;
    message += `✅ ${t(successful === 1 ? 'backup.restore.result.restored_one' : 'backup.restore.result.restored_other', { count: successful })}\n`;
    if (warnings > 0) {
      message += `⚠️ ${t(warnings === 1 ? 'backup.restore.result.warnings_one' : 'backup.restore.result.warnings_other', { count: warnings })}\n`;
    }
    if (skipped > 0) {
      message += `❌ ${t(skipped === 1 ? 'backup.restore.result.skipped_one' : 'backup.restore.result.skipped_other', { count: skipped })}\n`;
    }
    return message;
  };

  const handleApplyRestore = async () => {
    if (!importResult || !importResult.data) return;
    
    // Check if user has current data and overwrite is not checked
    if (hasCurrentData && !overwriteExisting) {
      setError(t('backup.restore.error.overwrite_required'));
      setShowRestorePreview(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await BackupService.applyImportedData(importResult.data as any, programTrack);
      
      if (result.success) {
        // Show detailed restore results
        if (result.results) {
          setSuccess(buildRestoreResultMessage(t('backup.restore.result.heading'), result.results));
        } else {
          setSuccess(t('backup.restore.completed'));
        }
        setShowRestorePreview(false);
        notifyStepWorkspaceChanged();
      } else {
        setError(result.error || t('backup.restore.failed'));
      }
    } catch (error) {
      setError(t('backup.restore.failed_with_reason', { error: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAndRestore = async () => {
    if (!importResult || !importResult.data) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First clear current data
      const clearResult = await BackupService.clearProgress(
        programTrack.program, 
        programTrack.track
      );
      
      if (!clearResult.success) {
        setError(clearResult.error || t('backup.restore.error.clear_failed'));
        return;
      }
      
      // Then restore new data
      const importResult2 = await BackupService.applyImportedData(importResult.data, programTrack);
      
      if (importResult2.success) {
        // Show detailed restore results
        if (importResult2.results) {
          setSuccess(buildRestoreResultMessage(t('backup.restore.result.heading_cleared'), importResult2.results));
        } else {
          setSuccess(t('backup.restore.completed_cleared'));
        }
        setShowRestorePreview(false);
        notifyStepWorkspaceChanged();
      } else {
        setError(importResult2.error || t('backup.restore.failed'));
      }
    } catch (error) {
      setError(t('backup.restore.clear_and_restore_failed', { error: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const formatBackupDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(getActiveLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStepsWithData = () => {
    if (!importResult?.data?.progress) return [];
    
    const stepsWithData = [];
    for (const [stepKey, stepData] of Object.entries(importResult.data.progress)) {
      const stepNumber = parseInt(stepKey.replace('step', ''));
      if (!isNaN(stepNumber)) {
        // Only include steps that have meaningful data (not "Not Started")
        const hasCompletedActions = stepData.data?.actions?.some((action: any) => action.completed);
        const isCompleted = stepData.completed;
        
        if (hasCompletedActions || isCompleted) {
          stepsWithData.push(stepNumber);
        }
      }
    }
    return stepsWithData.sort((a, b) => a - b);
  };

  const getInventorySummary = () => {
    if (!importResult?.data?.inventories) return { totalEntries: 0, totalInventories: 0 };
    
    const { resentments, fears, harms } = importResult.data.inventories;
    const totalEntries = (resentments?.length || 0) + (fears?.length || 0) + (harms?.length || 0);
    const totalInventories = [resentments, fears, harms].filter(arr => arr && arr.length > 0).length;
    
    return { totalEntries, totalInventories };
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>📥 {t('backup.restore.title')}</h3>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>
          
          <div className={styles.section}>
            <p>{t('backup.restore.intro', { label: programTrack.label })}</p>
            
            {hasCurrentData && (
              <div className={styles.warning}>
                ⚠️ {t('backup.restore.current_data_warning', { label: programTrack.label })}
              </div>
            )}
            
            <div className={styles.actionControls}>
              <button
                onClick={handleRestore}
                disabled={isLoading}
                className={styles.primaryButton}
              >
                {isLoading ? t('backup.processing') : getImportButtonText()}
              </button>
              <button
                onClick={onClose}
                className={styles.secondaryButton}
              >
                {t('dialog.cancel')}
              </button>
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              ❌ {error}
            </div>
          )}

          {success && (
            <div className={styles.successMessage}>
              ✅ {success}
            </div>
          )}
        </div>
      </div>

      {/* Restore Preview Modal */}
      {showRestorePreview && importResult && (
        <div className={styles.modalOverlay} onClick={() => setShowRestorePreview(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{t('backup.preview.title')}</h3>
              <button className={styles.closeButton} onClick={() => setShowRestorePreview(false)}>×</button>
            </div>
            
            <div className={styles.restorePreview}>
              <h4>{t('backup.preview.file_contents')}</h4>
              <div className={styles.previewContent}>
                <p><strong>{t('backup.preview.program')}</strong> {programTrack?.program || t('backup.preview.unknown')}</p>
                <p><strong>{t('backup.preview.track')}</strong> {programTrack?.track || t('backup.preview.unknown')}</p>
                <p><strong>{t('backup.preview.backup_date')}</strong> {importResult.summary?.exportDate ? formatBackupDate(importResult.summary.exportDate) : t('backup.preview.unknown')}</p>
                <p><strong>{t('backup.preview.steps_with_data')}</strong> {getStepsWithData().length > 0 ? getStepsWithData().join(', ') : t('backup.preview.none')}</p>
                <p><strong>{t('backup.preview.inventories')}</strong> {(() => {
                  const { totalEntries, totalInventories } = getInventorySummary();
                  if (totalEntries === 0) return t('backup.preview.inventories_none');
                  if (totalInventories === 1) {
                    return t(totalEntries === 1 ? 'backup.preview.entries_one' : 'backup.preview.entries_other', { count: totalEntries });
                  }
                  return t('backup.preview.entries_across', { count: totalEntries, inventories: totalInventories });
                })()}</p>
              </div>
             
              {/* Overwrite checkbox if there's existing data */}
              {hasCurrentData && (
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={overwriteExisting}
                      onChange={(e) => setOverwriteExisting(e.target.checked)}
                      className={styles.checkbox}
                    />
                    {t('backup.preview.overwrite_existing')}
                  </label>
                </div>
              )}
             
              {hasCurrentData ? (
                <div className={styles.restoreActions}>
                  <button
                    onClick={handleClearAndRestore}
                    disabled={isLoading}
                    className={styles.clearAndRestoreButton}
                  >
                    {isLoading ? t('backup.processing') : t('backup.preview.clear_and_restore')}
                  </button>
                  <button
                    onClick={() => setShowRestorePreview(false)}
                    className={styles.cancelButton}
                  >
                    {t('dialog.cancel')}
                  </button>
                </div>
              ) : (
                <div className={styles.restoreActions}>
                  <button
                    onClick={handleApplyRestore}
                    disabled={isLoading}
                    className={styles.applyRestoreButton}
                  >
                    {isLoading ? t('backup.preview.restoring') : t('backup.preview.restore_data')}
                  </button>
                  <button
                    onClick={() => setShowRestorePreview(false)}
                    className={styles.cancelButton}
                  >
                    {t('dialog.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Clear Modal Component
const ClearModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  programTrack: ProgramTrack;
}> = ({ isOpen, onClose, programTrack }) => {
  const { t } = useLocale();
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clearConfirmText, setClearConfirmText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
      setClearConfirmText('');
    }
  }, [isOpen]);

  const handleClear = async () => {
    if (clearConfirmText.trim() === '') {
      setError(t('backup.clear.error.confirm_required', { word: 'clear' }));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await BackupService.clearProgress(
        programTrack.program, 
        programTrack.track
      );
      
      if (result.success) {
        setSuccess(t('backup.clear.success'));
        notifyStepWorkspaceChanged();
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.error || t('backup.clear.failed'));
      }
    } catch (error) {
      setError(t('backup.clear.failed_with_reason', { error: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>🗑️ {t('backup.clear.title')}</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div className={styles.section}>
          <p>{t('backup.clear.intro', { label: programTrack.label })}</p>
          
          <div className={styles.warning}>
            ⚠️ {t('backup.clear.warning', { label: programTrack.label })}
          </div>
          
          <div className={styles.inputGroup}>
            <label htmlFor="clearConfirm">{t('backup.clear.confirm_label', { word: 'clear' })}</label>
            <input
              id="clearConfirm"
              type="text"
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              placeholder=""
              className={styles.confirmInput}
            />
          </div>
          
          <div className={styles.actionControls}>
            <button
              onClick={handleClear}
              disabled={isLoading || clearConfirmText.toLowerCase() !== 'clear'}
              className={styles.confirmClearButton}
            >
              {isLoading ? t('backup.clear.clearing') : t('backup.clear.clear_all')}
            </button>
            <button
              onClick={onClose}
              className={styles.cancelButton}
            >
              {t('dialog.cancel')}
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            ❌ {error}
          </div>
        )}

        {success && (
          <div className={styles.successMessage}>
            ✅ {success}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Modal Component
const BackupRestoreModal: React.FC<BackupRestoreModalProps> = ({ isOpen, onClose }) => {
  const { t } = useLocale();
  const { openStartOverModal } = useStartOverModal();
  const [selectedProgramTrack, setSelectedProgramTrack] = useState<ProgramTrack | null>(null);
  const [hasCurrentData, setHasCurrentData] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);

  // Available program-tracks (for now just SAA 12-steps)
  const availableProgramTracks: ProgramTrack[] = [
    {
      id: 'SAA-12-steps',
      label: 'SAA - 12 Steps',
      program: 'SAA',
      track: '12-steps'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      // Auto-select the first program-track
      setSelectedProgramTrack(availableProgramTracks[0]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedProgramTrack) {
      // Check if user has current data for this program-track
      checkCurrentData();
    }
  }, [selectedProgramTrack]);

  const checkCurrentData = async () => {
    if (!selectedProgramTrack) return;
    
    try {
      const hasData = await BackupService.hasCurrentData(
        selectedProgramTrack.program, 
        selectedProgramTrack.track
      );
      setHasCurrentData(hasData);
    } catch (error) {
      console.error('Error checking current data:', error);
    }
  };

  if (!isOpen) return null;

  const [eolBefore, eolAfter = ''] = t('backup.eol.body').split('{action}');

  return (
    <>
      {/* Main Modal */}
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>{t('backup.title')}</h2>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>

          <div className={styles.eolBanner}>
            <p>
              <strong>{t('backup.eol.title')}</strong> {eolBefore}
              <strong>{t('backup.eol.action')}</strong>{eolAfter}
            </p>
            <button
              type="button"
              className={styles.eolBannerButton}
              onClick={() => {
                onClose();
                openStartOverModal();
              }}
            >
              {t('backup.eol.open_button')}
            </button>
          </div>

          {/* Program-Track Selection */}
          <div className={styles.section}>
            <h3>{t('backup.select_program_track')}</h3>
            <div className={styles.programTrackSelector}>
              <select
                value={selectedProgramTrack?.id || ''}
                onChange={(e) => {
                  const selected = availableProgramTracks.find(pt => pt.id === e.target.value);
                  setSelectedProgramTrack(selected || null);
                }}
                className={styles.programTrackSelect}
              >
                {availableProgramTracks.map(pt => (
                  <option key={pt.id} value={pt.id}>
                    {pt.label}
                  </option>
                ))}
              </select>
              <p className={styles.moreComingSoon}>{t('backup.more_programs_soon')}</p>
            </div>
          </div>

          {/* Action Selection */}
          {selectedProgramTrack && (
            <div className={styles.section}>
              <h3>{t('backup.choose_action')}</h3>
              <div className={styles.actionButtons}>
                <button
                  onClick={() => setShowBackupModal(true)}
                  className={styles.actionButton}
                >
                  📤 {t('backup.backup.title')}
                </button>
                <button
                  onClick={() => setShowRestoreModal(true)}
                  className={styles.actionButton}
                >
                  📥 {t('backup.restore.title')}
                </button>
                <button
                  onClick={() => setShowClearModal(true)}
                  className={styles.actionButton}
                  disabled={!hasCurrentData}
                >
                  🗑️ {t('backup.clear.title')}
                </button>
              </div>
            </div>
          )}

          {/* Mobile Tips */}
          {isMobile() && (
            <div className={styles.mobileTips}>
              <h3>📱 {t('backup.mobile_tips')}</h3>
              <ul>
                {getFileOperationTips().map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Child Modals */}
      {selectedProgramTrack && (
        <>
          <BackupModal
            isOpen={showBackupModal}
            onClose={() => setShowBackupModal(false)}
            programTrack={selectedProgramTrack}
          />
          
          <RestoreModal
            isOpen={showRestoreModal}
            onClose={() => setShowRestoreModal(false)}
            programTrack={selectedProgramTrack}
            hasCurrentData={hasCurrentData}
          />
          
          <ClearModal
            isOpen={showClearModal}
            onClose={() => setShowClearModal(false)}
            programTrack={selectedProgramTrack}
          />
        </>
      )}
    </>
  );
};

export default BackupRestoreModal; 